import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import clsx from "clsx";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import LockIcon from "@material-ui/icons/Lock";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_Switch from "../../../Atoms/Inputs/Switches/A_Switch";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import ConfirmDialog from "../../../Molecules/Common/ProfessionalContacts/ConfirmDialog";
import {
  canDeleteContact,
  canDeleteAddress,
  canEditNoFurther,
  piiStatus,
  toDisplayDate,
} from "../../../../../domain/professionalContacts/professionalContacts.rules";
import {
  DISTRESSED_FIRM_TYPES,
  firmTypeLabel,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import { api, toFirmUpsert } from "./ProfessionalContacts.helper";
import IndividualsTable from "./IndividualsTable";
import AddressList from "./AddressList";
import BankAccountsTable from "./BankAccountsTable";
import DeletedContactsHistory from "./DeletedContactsHistory";
import DeletedAddressesHistory from "./DeletedAddressesHistory";
import DeletedBankAccountsHistory from "./DeletedBankAccountsHistory";
import AssociatedLoansPreview from "./AssociatedLoansPreview";
import AssociatedLoansDialog from "./AssociatedLoansDialog";
import StatusTag from "./StatusTag";
import useStyles from "./ProfessionalContacts.styles";

function deriveRoles(oidcUser) {
  const profile = oidcUser?.profile || {};
  if (Array.isArray(profile.roles)) return profile.roles;
  if (Array.isArray(profile.role)) return profile.role;
  if (typeof profile.role === "string") return [profile.role];
  return [];
}

const FirmRecord = ({ firmId, onBack, onEdit, onDeleted }) => {
  const classes = useStyles();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [dialog, setDialog] = useState({ open: false });
  const [showAssociatedLoans, setShowAssociatedLoans] = useState(false);

  const oidcUser = useSelector((state) => state.oidc && state.oidc.user);
  const canToggleNoFurther = canEditNoFurther({ roles: deriveRoles(oidcUser) });

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getFirm(firmId)
      .then(setFirm)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [firmId]);

  useEffect(() => {
    load();
  }, [load]);

  const closeDialog = () => setDialog({ open: false });

  const withBusy = async (fn) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const handleSetMain = (contactId) =>
    withBusy(async () => {
      const payload = toFirmUpsert({
        ...firm,
        contacts: firm.contacts.map((c) => ({
          ...c,
          isMain: c.id === contactId,
        })),
      });
      const updated = await api.updateFirm(firmId, payload);
      setFirm(updated);
    }).catch((err) => setActionError(err));

  const handleSetPreferred = (addressId) =>
    withBusy(async () => {
      const payload = toFirmUpsert({
        ...firm,
        addresses: firm.addresses.map((a) => ({
          ...a,
          isPreferred: a.id === addressId,
        })),
      });
      const updated = await api.updateFirm(firmId, payload);
      setFirm(updated);
    }).catch((err) => setActionError(err));

  const handleDeleteContact = (contact) => {
    if (!canDeleteContact(firm)) {
      setDialog({
        open: true,
        mode: "info",
        title: "Can't remove the last individual",
        message:
          "A company must keep at least one individual. Add another individual before deleting this one.",
      });
      return;
    }
    setDialog({
      open: true,
      mode: "confirm",
      title: "Delete Individual",
      message: (
        <>
          Delete individual <b>{contact.name || "(unnamed)"}</b>? A record is
          kept in the company&rsquo;s deleted-individuals history.
        </>
      ),
      onConfirm: () =>
        withBusy(async () => {
          const updated = await api.deleteContact(firmId, contact.id);
          setFirm(updated);
          closeDialog();
        }).catch((err) => {
          setActionError(err);
          closeDialog();
        }),
    });
  };

  const handleSetMainBankAccount = (bankAccountId) =>
    withBusy(async () => {
      const payload = toFirmUpsert({
        ...firm,
        bankAccounts: firm.bankAccounts.map((b) => ({
          ...b,
          isMain: b.id === bankAccountId,
        })),
      });
      const updated = await api.updateFirm(firmId, payload);
      setFirm(updated);
    }).catch((err) => setActionError(err));

  const handleDeleteBankAccount = (bankAccount) => {
    setDialog({
      open: true,
      mode: "confirm",
      title: "Delete Bank Account",
      message: (
        <>
          Delete the bank account for{" "}
          <b>{bankAccount.beneficiaryName || "(unnamed)"}</b>? A record is kept
          in the company&rsquo;s deleted-bank-account history.
        </>
      ),
      onConfirm: () =>
        withBusy(async () => {
          const updated = await api.deleteBankAccount(firmId, bankAccount.id);
          setFirm(updated);
          closeDialog();
        }).catch((err) => {
          setActionError(err);
          closeDialog();
        }),
    });
  };

  const handleDeleteAddress = (address) => {
    if (!canDeleteAddress(firm)) {
      setDialog({
        open: true,
        mode: "info",
        title: "Can't remove the last address",
        message:
          "A company must keep at least one address. Add another address before deleting this one.",
      });
      return;
    }
    setDialog({
      open: true,
      mode: "confirm",
      title: "Delete Address",
      message: "Delete this address?",
      onConfirm: () =>
        withBusy(async () => {
          const updated = await api.deleteAddress(firmId, address.id);
          setFirm(updated);
          closeDialog();
        }).catch((err) => {
          setActionError(err);
          closeDialog();
        }),
    });
  };

  const handleDeleteFirm = () => {
    setDialog({
      open: true,
      mode: "confirm",
      title: "Delete Company",
      message: (
        <>
          Delete <b>{firm.name}</b> ({firmTypeLabel(firm.type)})? This removes
          the company, its individuals, addresses and bank accounts from the
          central repository. This cannot be undone.
        </>
      ),
      onConfirm: () =>
        withBusy(async () => {
          try {
            await api.deleteFirm(firmId);
            closeDialog();
            onDeleted();
          } catch (err) {
            closeDialog();
            if (err.status === 409) {
              setDialog({
                open: true,
                mode: "info",
                title: "Can't delete this firm",
                message:
                  err.message ||
                  "This firm is assigned on one or more loans and cannot be deleted.",
              });
            } else {
              setActionError(err);
            }
          }
        }),
    });
  };

  const handleToggleFlag = (flagKey, nextValue) =>
    withBusy(async () => {
      try {
        const updated = await api.setFirmFlags(firmId, {
          [flagKey]: nextValue,
        });
        setFirm(updated);
      } catch (err) {
        if (err.status === 403) {
          setDialog({
            open: true,
            mode: "info",
            title: "Not permitted",
            message:
              "Only an Operational Supervisor can change 'No further instructions'.",
          });
        } else {
          setActionError(err);
        }
      }
    });

  if (loading) return <LoaderSkeleton />;

  return (
    <M_DataStateRenderer
      loading={false}
      error={error}
      data={firm}
      skeleton={<LoaderSkeleton />}
    >
      {firm && (
        <div className={classes.formShell}>
          <div className={classes.formTopBar}>
            <button
              type="button"
              aria-label="Back"
              title="Back"
              className={classes.formBackButton}
              onClick={onBack}
            >
              <ArrowBackIcon fontSize="small" />
            </button>
            <div className={classes.headerText}>
              <Typography component="h3" className={classes.formTitle}>
                {firm.name}
              </Typography>
              <div className={classes.subtle}>
                {firmTypeLabel(firm.type)}
                {firm.distressedOnly ? " · Distressed only" : ""}
              </div>
            </div>
            <div className={classes.headerActions}>
              <A_SecuredAction permission={pageSecurityActions.UPDATE}>
                <A_Button
                  label="Edit Record"
                  size="small"
                  premiumVariant="primary"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => onEdit(firmId)}
                  disabled={busy}
                />
              </A_SecuredAction>
              <A_SecuredAction permission={pageSecurityActions.DELETE}>
                <A_Button
                  label="Delete Company"
                  size="small"
                  premiumVariant="secondary"
                  className={classes.dangerButton}
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={handleDeleteFirm}
                  disabled={busy}
                />
              </A_SecuredAction>
            </div>
          </div>

          <div className={classes.formBody}>
            {actionError && (
              <div className={classes.validationSummary}>
                <ErrorOutlineIcon className={classes.validationIcon} />
                <span>
                  {actionError.message ||
                    "Something went wrong. Please try again."}
                </span>
              </div>
            )}

            <div className={classes.grid2}>
              <div>
                <div
                  className={clsx(
                    classes.sectionHeading,
                    classes.sectionHeadingFirst
                  )}
                >
                  Individuals ({firm.contacts.length})
                </div>
                <IndividualsTable
                  contacts={firm.contacts}
                  onSetMain={handleSetMain}
                  onDelete={handleDeleteContact}
                />

                <div className={classes.sectionHeading}>
                  Addresses ({firm.addresses.length})
                </div>
                <AddressList
                  addresses={firm.addresses}
                  onSetPreferred={handleSetPreferred}
                  onDelete={handleDeleteAddress}
                />

                <div className={classes.sectionHeading}>
                  Bank details ({(firm.bankAccounts || []).length})
                </div>
                <BankAccountsTable
                  bankAccounts={firm.bankAccounts || []}
                  onSetMain={handleSetMainBankAccount}
                  onDelete={handleDeleteBankAccount}
                />

                <DeletedContactsHistory
                  deletedContacts={firm.deletedContacts}
                />
                <DeletedAddressesHistory
                  deletedAddresses={firm.deletedAddresses}
                />
                <DeletedBankAccountsHistory
                  deletedBankAccounts={firm.deletedBankAccounts}
                />
              </div>

              <div>
                <div
                  className={clsx(
                    classes.sectionHeading,
                    classes.sectionHeadingFirst
                  )}
                >
                  Details &amp; flags
                </div>
                <div className={classes.sectionCard}>
                  <div className={classes.detailGrid}>
                    <div
                      className={clsx(
                        classes.detailRow,
                        classes.detailRowFlush
                      )}
                    >
                      <div className={classes.detailLabel}>Company contact</div>
                      <div className={classes.detailValue}>
                        {[firm.mainPhone, firm.email]
                          .filter(Boolean)
                          .join(" · ") || "-"}
                      </div>
                    </div>

                    {firm.type !== "BorrowerSolicitor" && (
                      <>
                        <div
                          className={clsx(
                            classes.detailRow,
                            classes.detailRowFlush
                          )}
                        >
                          <div className={classes.detailLabel}>
                            Professional indemnity insurance
                          </div>
                          <div className={classes.detailValue}>
                            <span>
                              {firm.piiValue
                                ? `£${Number(firm.piiValue).toLocaleString(
                                    "en-GB"
                                  )}`
                                : "-"}
                              {firm.piiExpiry
                                ? ` · valid to ${toDisplayDate(firm.piiExpiry)}`
                                : ""}
                            </span>
                            {firm.piiValue ? (
                              <StatusTag
                                tone={
                                  piiStatus(firm) === "Valid"
                                    ? "valid"
                                    : "expiring"
                                }
                              >
                                {piiStatus(firm)}
                              </StatusTag>
                            ) : null}
                          </div>
                        </div>
                        <div
                          className={clsx(
                            classes.detailRow,
                            classes.detailRowFlush
                          )}
                        >
                          <div className={classes.detailLabel}>
                            Panel approved
                          </div>
                          <div className={classes.detailValue}>
                            {firm.panelApproved ? (
                              <>
                                <StatusTag tone="valid">Yes</StatusTag>
                                <span>
                                  {toDisplayDate(firm.panelApprovedDate) || "-"}
                                </span>
                              </>
                            ) : (
                              <StatusTag tone="neutral">No</StatusTag>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    <div
                      className={clsx(
                        classes.detailRow,
                        classes.detailRowFlush,
                        classes.detailRowWide
                      )}
                    >
                      <div className={classes.detailLabel}>Notes</div>
                      <div className={classes.detailValue}>
                        {firm.notes || (
                          <span className={classes.subtle}>-</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={classes.flagsRow}>
                    <div className={classes.toggleWrap}>
                      <A_Switch
                        label="Active"
                        labelPlacement="end"
                        size="small"
                        checked={Boolean(firm.active)}
                        disabled={busy}
                        onChange={(e) =>
                          handleToggleFlag("active", e.target.checked)
                        }
                      />
                    </div>
                    <div className={classes.toggleWrap}>
                      <A_Switch
                        label={
                          <span className={classes.lockedLabel}>
                            No further instructions
                            <Tooltip
                              title="Only an Operational Supervisor can change 'No further instructions'."
                              placement="top"
                            >
                              <LockIcon className={classes.lockedLabelIcon} />
                            </Tooltip>
                          </span>
                        }
                        labelPlacement="end"
                        size="small"
                        checked={Boolean(firm.noFurtherInstructions)}
                        disabled={busy || !canToggleNoFurther}
                        onChange={(e) =>
                          handleToggleFlag(
                            "noFurtherInstructions",
                            e.target.checked
                          )
                        }
                      />
                    </div>
                    {(DISTRESSED_FIRM_TYPES.includes(firm.type) ||
                      firm.distressedOnly) && (
                      <div className={classes.toggleWrap}>
                        <A_Switch
                          label="Distressed Only"
                          labelPlacement="end"
                          size="small"
                          checked={Boolean(firm.distressedOnly)}
                          disabled={busy}
                          onChange={(e) =>
                            handleToggleFlag("distressedOnly", e.target.checked)
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                <AssociatedLoansPreview
                  firmId={firmId}
                  associatedLoanCount={firm.associatedLoanCount}
                  onViewAll={() => setShowAssociatedLoans(true)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={dialog.open}
        mode={dialog.mode}
        title={dialog.title}
        message={dialog.message}
        onConfirm={dialog.onConfirm}
        onClose={closeDialog}
      />
      {showAssociatedLoans && (
        <AssociatedLoansDialog
          firmId={firmId}
          firmName={firm?.name}
          onClose={() => setShowAssociatedLoans(false)}
        />
      )}
    </M_DataStateRenderer>
  );
};

FirmRecord.propTypes = {
  firmId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

export default FirmRecord;
